import { motion } from "framer-motion";
import { MapPin, Utensils, Trees, Building2, Bed } from "lucide-react";
import { Reveal, Overline } from "./Reveal";

const images = [
  { src: "/brand/masseria/landscape.jpg", alt: "Ulivo secolare e muro in pietra", span: "md:col-span-7 md:row-span-2 aspect-[16/11] md:aspect-auto" },
  { src: "/brand/masseria/esterni-vertical.jpg", alt: "Vista sulla corte interna", span: "md:col-span-5 md:row-span-2 aspect-[3/4] md:aspect-auto" },
  { src: "/brand/masseria/interni.jpg", alt: "Spazio comune interno", span: "md:col-span-4 aspect-[4/3]" },
  { src: "/brand/masseria/apparecchiatura.jpg", alt: "Pranzo in masseria", span: "md:col-span-4 aspect-[4/3]" },
  { src: "/brand/masseria/garden.jpg", alt: "Camere della masseria", span: "md:col-span-4 aspect-[4/3]" },
];

const features = [
  { icon: MapPin, label: "Palmariggi (Lecce) · Puglia" },
  { icon: Bed, label: "Camere residenziali per la full immersion" },
  { icon: Utensils, label: "Volìvo Restaurant — opzionale cena in struttura" },
  { icon: Trees, label: "Ulivi secolari, giardino e spazi comuni 24/7" },
  { icon: Building2, label: "Aule laboratorio nella stessa struttura" },
];

export const Location = () => (
  <section id="location" className="relative bg-white py-24 sm:py-32" data-testid="location-section">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <div className="grid gap-10 lg:grid-cols-12 lg:items-end">
        <div className="lg:col-span-7">
          <Reveal>
            <Overline>La location</Overline>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-5 font-display text-4xl font-black uppercase leading-[0.98] tracking-tight text-[#16256B] sm:text-5xl lg:text-6xl">
              Secolario <span className="text-[#C9A227]">&mdash; Masseria del Viverbene</span>.
              <br />
              Dove le idee prendono forma.
            </h2>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-6 max-w-xl font-body text-base leading-relaxed text-[#4A5268]">
              Un&rsquo;antica masseria salentina immersa tra ulivi secolari e muri in
              pietra bianca. <strong className="text-[#16256B]">Qui si vive, si lavora e si cresce insieme</strong>:
              aule, cene, camere e giardino nello stesso luogo. I rapporti con i
              colleghi diventano parte dell&rsquo;esperienza &mdash; e restano quando il
              corso finisce.
            </p>
          </Reveal>
        </div>

        <div className="lg:col-span-5">
          <Reveal delay={0.12}>
            <ul className="grid gap-3">
              {features.map((f) => (
                <li
                  key={f.label}
                  className="flex items-center gap-4 rounded-xl border border-[#E4E8F2] bg-[#F7F9FD] px-4 py-3"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#16256B]/5">
                    <f.icon className="h-4 w-4 text-[#16256B]" strokeWidth={1.75} />
                  </span>
                  <span className="font-body text-sm text-[#16256B]">{f.label}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </div>

      {/* Bento gallery */}
      <div className="mt-14 grid gap-4 md:grid-cols-12 md:grid-rows-[1fr_1fr] md:gap-5">
        {images.map((img, i) => (
          <motion.div
            key={img.src}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
            className={`${img.span} group relative overflow-hidden rounded-2xl`}
            data-testid={`location-image-${i}`}
          >
            <img
              src={img.src}
              alt={img.alt}
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.06]"
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0E1A4A]/40 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
          </motion.div>
        ))}
      </div>

      {/* Address block */}
      <div className="mt-14 grid gap-6 rounded-3xl border border-[#E4E8F2] bg-[#F7F9FD] p-8 sm:grid-cols-[1fr_auto] sm:items-center sm:gap-10 sm:p-10">
        <div>
          <p className="font-body text-xs uppercase tracking-overline text-[#C9A227]">Indirizzo</p>
          <p className="mt-3 font-display text-xl font-extrabold text-[#16256B] sm:text-2xl">
            Secolario &mdash; Masseria del Viverbene
          </p>
          <p className="mt-1 font-body text-sm text-[#4A5268] sm:text-base">
            Loc. Alti Pareti 73.20 · Palmariggi (Le) · Puglia
          </p>
        </div>
        <a
          href="https://www.google.com/maps/search/Masseria+Secolario+Palmariggi"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-[#16256B] px-6 py-3 font-display text-sm font-bold text-white transition-colors duration-200 hover:bg-[#0E1A4A]"
          data-testid="location-maps-link"
        >
          <MapPin className="h-4 w-4" />
          Apri in Google Maps
        </a>
      </div>
    </div>
  </section>
);
